<template>
  <b-modal v-model="isModalVisible" size="md" dialog-class="mt-md-5 p-0 rounded-5" content-class="p-2"
    footer-class="boarder-top-0" model-class="modal-custom" header-class="border-bottom-0" @ok="submitFiles"
    @cancel="reset">

    <template v-slot:modal-header>
      <h5 class="mb-0 text-uppercase font-weight-bold">
        {{ $t("page.newProject.newAnalysis") }}
      </h5>
    </template>

    <!-- File selection -->
    <template v-slot:default v-if="currentStep === 'file-selection'">
      <p class="font-weight-bold label">
        {{ $t("name") }}
      </p>
      <b-form-input v-model="newProjectName"> </b-form-input>

      <!-- Drag and drop -->
      <div class="upload-section-wrapper mt-4 rounded" :class="{ 'custom-shadow': dragoverActive }">
        <div @click="openUploadDialog" class="upload-section rounded h-100" @drop.prevent="uploadLocalFile"
          @dragover.prevent="dragover" @dragenter="dragoverActive = true" @dragleave="dragoverActive = false">
          <input :accept="getMimeTypes" ref="inputUpload" type="file" multiple name="file-input"
            @change="uploadLocalFile" />
          <b-row class="no-gutters h-100 justify-content-center" align-v="center">
            <b-col class="col-12 text-center">
              <img src="./../../assets/general-icons/upload-alt.svg" class="upload__icon" />
              <p class="mb-0 font-weight-bold text-emotiva-pink">
                {{ dropFileMsg }}
              </p>
              <p class="mb-1 text-emotiva-pink">{{ $t("or") }}</p>
              <p variant="outline-secondary" class="mx-auto text-emotiva-pink" size="sm">
                {{ $t("button.chooseFile") }}
              </p>
            </b-col>
          </b-row>
        </div>
      </div>
      <!-- Drag and drop -->

      <!-- Warning if not enough analyses -->
      <div class="analysis-summary" v-if="!isUnlimitedSubscription">
        <div>
          <p v-if="!canBuy" class="text-danger mb-1">
            {{ $t("page.newProject.insufficientAnalysis", { needed: totalCost, available: availableAnalysis }) }}
          </p>
        </div>

        <!-- NORMAL state -->
        <div v-if="canBuy && totalCost > 0">
          <p class="text-muted mb-1">
            {{ $t("page.newProject.costAnalysis", { totalCost: totalCost }) }}
          </p>
          <p class="small">
            {{ $t("page.newProject.remainingAnalysis", { remaining: availableAnalysis - totalCost }) }}
          </p>
        </div>

      </div>
      <!-- Warning if not enough analyses -->

      <!-- Preview -->
      <div class="video-preview mt-5" v-if="fileArray.length > 0">
        <div v-for="(fileInfo, index) in fileArray" :key="index">
          <b-row v-if="loadingVideo && !fileInfo.loaded" class="no-gutters h-100 w-100 mt-3" align-v="center">
            <b-col class="col-12 text-center text-emotiva-pink">
              <b-icon icon="arrow-clockwise" animation="spin-pulse" font-scale="4" variant="white"></b-icon>
              <div class="text-uppercase">
                {{ $t("loading") }}
              </div>
            </b-col>
          </b-row>

          <b-row class="mt-2 align-items-center file-item-row">
            <b-col class="d-flex p-2 align-items-center justify-content-between emotiva-pink-background-light">
              <div class="d-flex align-items-center overflow-hidden">
                <!-- THUMBNAIL -->
                <span v-if="fileInfo.type && fileInfo.type.startsWith('image/')">
                  <img :src="fileInfo.src" alt="" :ref="`image_${index}`" class="small-image mt-1 ml-3 mr-3"
                    @load="onImageLoad($event, index)" />
                </span>
                <span v-else-if="fileInfo.type && fileInfo.type.startsWith('video/')">
                  <video class="small-image mt-1 ml-3 mr-3" preload="metadata" :ref="`video_${index}`" hidden
                    @canplay="onVideoLoad($event, index)" :src="`${fileInfo.src}#t=1.5`"></video>
                  <canvas hidden :ref="`canvas_${index}`" class="small-image mt-1 ml-3 mr-3"></canvas>
                </span>

                <!-- FILENAME -->
                <span class="file-name text-truncate">{{ fileInfo.name }}</span>
              </div>

              <!-- Satus -->
              <div class="d-flex align-items-center">
                <b-icon v-if="fileInfo.uploadStatus === 'uploading'" icon="arrow-clockwise" animation="spin-pulse"
                  class="mr-2" font-scale="1.2" variant="primary"></b-icon>
                <b-icon v-if="fileInfo.uploadStatus === 'success'" icon="check-circle" class="mr-2" font-scale="1.2"
                  variant="success"></b-icon>
                <b-icon v-else-if="fileInfo.uploadStatus === 'error'" icon="exclamation-circle" class="mr-2"
                  font-scale="1.2" variant="danger"></b-icon>

                <b-button v-if="fileInfo.uploadStatus === 'error'" variant="outline-danger" size="sm" class="p-1 mr-2"
                  @click="retryUpload(index)">
                  <b-icon icon="arrow-repeat" font-scale="0"></b-icon>
                </b-button>

                <b-icon icon="x" @click="removeFile(index)" style="cursor: pointer" font-scale="1.5"
                  class="mr-4"></b-icon>
              </div>
            </b-col>
          </b-row>

        </div>
      </div>
      <!-- Preview -->

    </template>
    <!-- File selection -->

    <!-- Viewer selection -->
    <template v-if="currentStep === 'viewer-selection'">
      <p class="font-weight-bold">{{ $t("page.newProject.projectType") }}</p>
      <b-row>
        <b-col :class="[
          'viewer-option m-2',
          selectedViewer === viewerItem.value ? 'selected-viewer' : ''
        ]" v-for="(viewerItem, index) in viewers" :key="index" @click="setViewer(viewerItem.value)">
          <img class="viewer-icon" :src="viewerItem.icon" alt="" />
          <h6 class="mt-2">{{ viewerItem.name }}</h6>
        </b-col>
      </b-row>
    </template>

    <template v-if="currentStep === 'analysisInProgress'"> </template>
    <!-- Viewer selection -->

    <template v-slot:modal-footer="{ ok, cancel }">
      <!-- Continue -->
      <div class="footer-align-right">
        <div v-if="currentStep === 'viewer-selection'">
          <b-button variant="none" style="color:var(--emotiva-white-text)"
            class="mr-3 justify-content-center align-items-center bold action-btn btn-blue-border" @click="cancel">{{
              $t("button.cancel") }}</b-button>
          <b-button variant="none" style="color: #fff;"
            class="justify-content-center align-items-center bold emotiva-pink-background action-btn"
            :disabled="selectedViewer === ''" @click="nextStep('file-selection')">{{ $t("button.continue") }}</b-button>
        </div>
        <!-- Continue -->

        <!-- Start analysis -->
        <div v-else>
          <b-button variant="white"
            class="mr-3 action-btn justify-content-center align-items-center bold btn-blue-border" @click="cancel">{{
              $t("button.cancel") }}</b-button>
          <b-button variant="none" style="color:#fff"
            class="justify-content-center align-items-center bold emotiva-pink-background action-btn"
            :disabled="!isFormValid() || selectedViewer === '' || isUploading" @click="submitFiles">
            <img src="../../assets/general-icons/startAnalysis-icon.svg" alt="" />
            <span class="ml-2">{{ isUploading ? $t("button.uploading") : $t("button.startAnalysis") }}</span>
          </b-button>
        </div>
      </div>
      <!-- Start analysis -->
    </template>


  </b-modal>
</template>

<script>
import { uniqueId } from "lodash";
export default {
  props: {
    modalStatus: {
      type: Boolean,
      required: true,
    },
    projectName: {
      type: String,
      required: false,
      default: "",
    },
    projectType: {
      type: String,
      required: true,
    },
    folderId: {
      type: String,
      required: false,
    }
  },
  data() {
    return {
      analysisType: ["Standard", "Type1", "Type2", "Type3"],
      loadingVideo: false,
      showVideoCaption: false,
      dragoverActive: false,
      fileArray: [],
      thumbnailBase64: "",
      scale: 0.25,
      totalAssets: 0,
      isUploading: false,
      mimeTypes: ['video/mp4', 'video/quicktime', 'image/jpeg', 'image/png'
        , 'video/mp5'
        , 'image/gif'
        , 'video/webm'
        , 'image/webp'
      ],
      newProjectName: "",
      steps: ["file-selection", "viewer-selection"],
      currentStep: "viewer-selection",
      viewers: [
        {
          name: this.$t("standard"),
          value: "standard",
          icon: require("../../assets/viewer-icons/standard-viewer.svg"),
        },
        {
          name: this.$t("instagram"),
          value: "instagram",
          icon: require("../../assets/viewer-icons/instagram-viewer.svg"),
        },
        {
          name: this.$t("facebook"),
          value: "facebook",
          icon: require("../../assets/viewer-icons/facebook-viewer.svg"),
        },
        // {
        //   name: this.$t("youtube"),
        //   value: "youtube",
        //   icon: require("../../assets/viewer-icons/youtube-viewer.svg"),
        // },
        {
          name: this.$t("X"),
          value: "x",
          icon: require("../../assets/viewer-icons/x-viewer.svg"),
        },
        {
          name: this.$t("tiktok"),
          value: "tiktok",
          icon: require("../../assets/viewer-icons/tiktok-viewer.svg"),
        },
      ],
      selectedViewer: "",
      // mimeTypes: ["image/*", "video/*"],
    };
  },
  computed: {
    activeSubscription() {
      return this.$store.getters['subscription/getActiveSubscription']
    },
    availableAnalysis() {
      return this.activeSubscription.analysisLeft;
    },
    isUnlimitedSubscription(){
      return this.activeSubscription.isUlimited;
    },
    canBuy() {
      if (this.isUnlimitedSubscription) return true
      return this.availableAnalysis >= this.fileArray.filter(file =>
        file.uploadStatus === 'pending').length
    },
    totalCost() {
      return this.fileArray.length;
    },
    isModalVisible: {
      get() {
        return this.modalStatus;
      },
      set(modalStatus) {
        this.$emit("setModalStatus", modalStatus);
      },
    },

    getMimeTypes() {
      return this.mimeTypes.join(", ");
    },

    dropFileMsg() {
      return this.$t("page.stimulus.dropFile");
    },

    pendingUploads() {
      return this.fileArray.filter(file =>
        file.uploadStatus === 'pending' || file.uploadStatus === 'uploading');
    },

    failedUploads() {
      return this.fileArray.filter(file => file.uploadStatus === 'error');
    },

    successfulUploads() {
      return this.fileArray.filter(file => file.uploadStatus === 'success');
    }
  },
  methods: {
    isFormValid() {

      return this.newProjectName.trim() !== "" && this.fileArray.length > 0 && this.canBuy;
    },

    async submitFiles() {
      if (this.isUploading) return;

      this.isUploading = true;

      // If we have more than one file
      // First a folder with the name of the analysis
      // Then rename each analysis for the file with the name of the video
      if (this.fileArray.length > 1) {
        const payload = {
          name: this.newProjectName,
          description: ''
        }
        let activeFolder = this.folderId !== ""
        if (this.folderId) {

        } else {
          try {
            await this.$store.dispatch('folder/createFolderByClientId', payload).then(() => {
              this.$store.dispatch("folder/retrieveFoldersByClientId");
            });
            activeFolder = await this.$store.getters['folder/getActiveFolder'];
            this.folderId = activeFolder._id
          } catch (err) {
            // If not in the folder creation fails, just create the projects in the dashboard
            console.error("error while creating a folder: ", err);
          }
        }
      }

      // Set all pending files to uploading status
      this.fileArray.forEach(file => {
        if (file.uploadStatus !== 'success') {
          file.uploadStatus = 'uploading';
        }
      });

      // Process each file sequentially
      for (let i = 0; i < this.fileArray.length; i++) {
        const fileInfo = this.fileArray[i];

        // Skip already uploaded files
        if (fileInfo.uploadStatus === 'success') continue;

        await this.uploadSingleFile(i);
      }

      this.isUploading = false;

      // If all files were uploaded successfully, complete the process
      if (this.failedUploads.length === 0) {
        this.$emit("submit");
        this.reset();
      }
    },

    async uploadSingleFile(index) {
      const fileInfo = this.fileArray[index];
      const payload = {
        viewer: this.selectedViewer,
        file: fileInfo,
        projectName: `${this.newProjectName}${this.fileArray.length > 1 ? ` - ${fileInfo.name}` : this.newProjectName}`,
        folderId: this.folderId,
        thumbnail: fileInfo.thumbnail
      };

      try {
        await this.$store.dispatch("project/createPredictProjectByClientId", payload);
        // Update the file's status to success
        this.$set(this.fileArray[index], 'uploadStatus', 'success');
        return true;
      } catch (error) {
        console.error(`Error for video: ${this.fileArray[index]}, err:`, error)
        // Update the file's status to error
        this.$set(this.fileArray[index], 'uploadStatus', 'error');

        // Handle specific errors
        if (error.response && error.response.status === 403) {
          this.$store.commit("error/SET_ERROR", {
            titleText: 'dialog.error.subscriptionExpiredMessage.titleText',
            messageText: "dialog.error.subscriptionExpiredMessage.messageText"
          }, { root: true });
        } else {
          this.$store.commit("error/SET_ERROR", {
            titleText: "dialog.error.subscriptionNotFoundMessage.titleText",
            messageText: "dialog.error.subscriptionNotFoundMessage.messageText"
          }, { root: true });
        }
        return false;
      }
    },

    async retryUpload(index) {
      this.$set(this.fileArray[index], 'uploadStatus', 'uploading');
      await this.uploadSingleFile(index);

      // After all retries, refresh subscription data
      this.$store.dispatch("subscription/retrieveActiveSubscriptionByClientId")
        .catch((e) => console.error('Something went wrong when fetching subscription: ', e));
    },

    nextStep(step) {
      this.currentStep = step;
    },

    uploadLocalFile(e) {
      const errorFiles = [];
      const src = e.dataTransfer || e.target;
      const uploadedFiles = src.files;

      if (!uploadedFiles || uploadedFiles.length === 0) {
        console.log("No uploaded files, e: ", e);
        return;
      }

      // Process each file
      for (const uploadedFile of uploadedFiles) {
        // Check file size
        const MAX_MB_SIZE = 50;
        if (uploadedFile.size / Math.pow(1024, 2) > MAX_MB_SIZE) {
          errorFiles.push(uploadedFile.name);
          continue;
        }

        // Check file type
        if (!this.isValidFileType(uploadedFile.type)) {
          errorFiles.push(uploadedFile.name);
          continue;
        }

        // Create a new file info object
        const fileInfo = {
          _id: uniqueId("file_"),
          name: uploadedFile.name,
          size: uploadedFile.size,
          type: uploadedFile.type,
          file: uploadedFile,
          src: null,
          thumbnailBase64: null,
          loaded: false,
          uploadStatus: 'pending'  // New property to track upload status: pending, uploading, success, error
        };

        this.fileArray.push(fileInfo);
      }

      // Show errors if any
      if (errorFiles.length > 0) {
        this.$store.commit("error/SET_ERROR", {
          messageText: `The following files are not of the correct type or size and are not included: ${errorFiles.join(', ')}`,
        });
      }

      // If no valid files were added, exit
      if (this.fileArray.length === 0) {
        return;
      }

      this.dragoverActive = false;
      this.loadingVideo = true;
      this.setDataURL();
    },

    onImageLoad(event, index) {
      // Find the fileInfo object that corresponds to this image
      const fileInfo = this.fileArray[index];
      if (!fileInfo) return;

      this.loadingVideo = false;
      fileInfo.loaded = true;

      // Store this particular file as the project thumbnail, maybe lower the quality
      // this.$store.commit("project/SET_PROJECT_THUMBNAIL", fileInfo.file);
      this.fileArray[index].thumbnail = fileInfo.file
      this.showVideoCaption = true;
    },

    onVideoLoad(event, index) {
      const fileInfo = this.fileArray[index];
      if (!fileInfo) return;

      this.loadingVideo = false;
      this.showVideoCaption = true;
      fileInfo.loaded = true;

      // Get the corresponding video and canvas elements
      const videoRef = `video_${index}`;
      const canvasRef = `canvas_${index}`;

      const video = this.$refs[videoRef] ? this.$refs[videoRef][0] : null;
      const canvas = this.$refs[canvasRef] ? this.$refs[canvasRef][0] : null;

      if (!video || !canvas) {
        console.error("Video or canvas reference not found", videoRef, canvasRef);
        return;
      }

      video.hidden = false;

      const context = canvas.getContext("2d");
      const MAX_WIDTH = 640;
      // const filename = `thumbnail_${index}`;

      if (!video.videoWidth || !video.videoHeight) {
        console.warn("Video dimensions are 0. Drawing fallback thumbnail.");
        canvas.width = 640;
        canvas.height = 360;

        context.fillStyle = "#6200ee";
        context.fillRect(0, 0, canvas.width, canvas.height);

        const logo = new Image();
        logo.src = require('../../assets/menu-icons/MENU_logo_login.png');
        logo.onload = () => {
          const logoWidth = 280;
          const logoHeight = 160;
          const centerX = (canvas.width - logoWidth) / 2;
          const centerY = (canvas.height - logoHeight) / 2;

          context.drawImage(logo, centerX, centerY, logoWidth, logoHeight);

          fileInfo.thumbnailBase64 = canvas.toDataURL();
          this.fileArray[index].thumbnail = this.base64ToFile(fileInfo.thumbnailBase64, `defaultProjectThumbnail_${index}`)
          //   this.$store.commit("project/SET_PROJECT_THUMBNAIL",
          //     this.base64ToFile(fileInfo.thumbnailBase64, `defaultProjectThumbnail_${index}`));
        };
      } else {
        // Video exists, draw and scale it
        const scale = MAX_WIDTH / video.videoWidth;
        const scaledWidth = MAX_WIDTH;
        const scaledHeight = Math.floor(video.videoHeight * scale);

        canvas.width = scaledWidth;
        canvas.height = scaledHeight;

        context.drawImage(video, 0, 0, scaledWidth, scaledHeight);

        fileInfo.thumbnailBase64 = canvas.toDataURL();
        this.fileArray[index].thumbnail = this.base64ToFile(fileInfo.thumbnailBase64, `projectThumbnail_${index}`);
        // this.$store.commit("project/SET_PROJECT_THUMBNAIL",
        //   this.base64ToFile(fileInfo.thumbnailBase64, `projectThumbnail_${index}`));
      }
    },

    base64ToFile(base64String, filename = "projectThumbnail") {
      const arr = base64String?.split(',');
      if (!arr || arr.length < 2 || !arr[0] || !arr[1]) {
        console.error('Invalid base64 format:', base64String);
        return null;
      }

      const mimeMatch = arr[0].match(/:(.*?);/);
      if (!mimeMatch) {
        console.error('Could not extract MIME type from base64 string:', arr[0]);
        return null;
      }

      const mime = mimeMatch[1];
      const bstr = atob(arr[1]);
      let n = bstr.length;
      const u8arr = new Uint8Array(n);
      while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
      }
      return new File([u8arr], `${filename}.png`, { type: mime });
    },

    openUploadDialog() {
      this.$refs.inputUpload.click();
    },

    isValidFileType(type) {
      return this.mimeTypes.includes(type);
    },

    dragover(event) {
      event.dataTransfer.dropEffect = "move";
    },

    setDataURL() {
      // Create object URLs for each file
      this.fileArray.forEach(fileInfo => {
        if (!fileInfo.src) {
          fileInfo.src = URL.createObjectURL(fileInfo.file);
        }
      });
    },

    removeFile(index) {
      // Revoke object URL before removing the file
      if (this.fileArray[index] && this.fileArray[index].src) {
        URL.revokeObjectURL(this.fileArray[index].src);
      }

      // Remove the file from the array
      this.fileArray.splice(index, 1);
    },

    resetUploadForm() {
      // Clean up all object URLs
      this.fileArray.forEach(fileInfo => {
        if (fileInfo.src) {
          URL.revokeObjectURL(fileInfo.src);
        }
      });

      this.fileArray = [];
      this.loadingVideo = false;
      this.showVideoCaption = false;
    },

    setViewer(viewer) {
      this.selectedViewer = viewer;
    },

    reset() {
      this.resetUploadForm();
      this.newProjectName = "";
      this.selectedViewer = "";
      this.isUploading = false;
      this.$emit("setModalStatus", false);
      this.nextStep('viewer-selection');
    }
  },
};
</script>

<style scoped>
.footer-align-right {
  display: flex;
  justify-content: flex-end;
}

.textOverflow {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 60%;
}

.viewer-option {
  border: 1px solid #ccc;
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 96px;
  min-width: 140px;
}

.selected-viewer {
  border-color: var(--emotiva-pink);
  background-color: rgba(238, 100, 149, 0.1);
}

.viewer-icon {
  width: 22px;
  height: 22px;
}

.upload-section-wrapper {
  border: 2px dashed var(--emotiva-pink);
  height: 141px;
  transition: all 0.3s ease;
}

.upload-section {
  height: 100%;
  cursor: pointer;
}

.upload-section input {
  display: none;
}

.upload__icon {
  width: 32px;
  height: 32px;
  margin-bottom: 8px;
}

.custom-shadow {
  box-shadow: 0 0 10px rgba(238, 100, 149, 0.5);
  border-color: var(--emotiva-pink);
}

.emotiva-pink-background-light {
  /* background-color: rgba(238, 100, 149, 0.1); */
  /* background-color: var(--emotiva-pink); */
  border-radius: 4px;
  padding: 8px;
}

.action-btn {
  border-radius: 26px;
  height: 46px;
  min-width: 100px;
}

.btn-blue-border {
  border: 2px solid #cbd2e0;
  border-color: #cbd2e0;
}

.video-preview {
  max-height: 300px;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.video-preview::-webkit-scrollbar {
  display: none;
}

/* Thumbnails */
.small-image {
  width: 50px;
  height: 50px;
  object-fit: cover;
  border-radius: 4px;
}

/* File Name - truncate if too long */
.file-name {
  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Row appearance */
.file-item-row {
  border-radius: 8px;
  margin-bottom: 8px;
}
</style>